import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Goal, GoalResponse } from "../types";
import { getGoalHistory } from "../repository/daysRepository";

const GoalHistory: React.FC = () => {
  const { goalId } = useParams<{ goalId: string }>();
  const navigate = useNavigate();
  const [goal, setGoal] = useState<Goal | null>(null);
  const [responses, setResponses] = useState<GoalResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    if (!goalId) return;
    try {
      const data = await getGoalHistory(goalId);
      setGoal(data.goal);
      setResponses(data.responses.sort((a, b) => (b.date > a.date ? 1 : -1)));
    } catch (err) {
      console.error("Failed to load history:", err);
    } finally {
      setLoading(false);
    }
  }, [goalId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!goal) {
    return <div className="empty-state">Goal not found.</div>;
  }

  const totalResponses = responses.length;
  let successCount = 0;
  let totalValue = 0;

  if (goal.goal_type === "binary") {
    successCount = responses.filter((r) => r.value === true).length;
  } else {
    responses.forEach((r) => {
      totalValue += Number(r.value) || 0;
      if (goal.target && Number(r.value) >= goal.target) {
        successCount++;
      }
    });
  }

  const completionRate = totalResponses > 0 ? Math.round((successCount / totalResponses) * 100) : 0;

  // Calculate current streak
  let streak = 0;
  const sorted = [...responses].sort((a, b) => (b.date > a.date ? 1 : -1));
  for (const r of sorted) {
    const hit = goal.goal_type === "binary"
      ? r.value === true
      : (goal.target ? Number(r.value) >= goal.target : Number(r.value) > 0);
    if (hit) streak++;
    else break;
  }

  return (
    <div className="history-page">
      <button className="btn-back" onClick={() => navigate("/goals")}>&larr; Back to Goals</button>

      <div className="history-header">
        <h2>{goal.title}</h2>
        {goal.description && <p className="history-desc">{goal.description}</p>}
        <div className="history-meta">
          <span>{goal.goal_type === "binary" ? "Yes/No" : `Numeric (target: ${goal.target})`}</span>
          <span>{goal.frequency_days === 1 ? "Daily" : `Every ${goal.frequency_days} days`}</span>
          <span className={`status-badge status-${goal.status}`}>{goal.status}</span>
        </div>
      </div>

      <div className="history-stats">
        <div className="stat-card">
          <div className="stat-value">{totalResponses}</div>
          <div className="stat-label">Entries</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{completionRate}%</div>
          <div className="stat-label">Hit Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{streak}</div>
          <div className="stat-label">Current Streak</div>
        </div>
        {goal.goal_type === "numeric" && totalResponses > 0 && (
          <div className="stat-card">
            <div className="stat-value">{(totalValue / totalResponses).toFixed(1)}</div>
            <div className="stat-label">Average</div>
          </div>
        )}
      </div>

      <div className="history-list">
        <h3>Recent Entries</h3>
        {responses.length === 0 && <p className="muted">No entries yet.</p>}
        {responses.map((r) => {
          const hit = goal.goal_type === "binary"
            ? r.value === true
            : (goal.target ? Number(r.value) >= goal.target : Number(r.value) > 0);

          return (
            <div key={r.uuid} className={`history-row ${hit ? "hit" : "miss"}`}>
              <span className="history-date">{r.date}</span>
              <span className="history-value">
                {goal.goal_type === "binary" ? (r.value ? "Yes" : "No") : r.value}
                {goal.goal_type === "numeric" && goal.target && (
                  <span className="history-target"> / {goal.target}</span>
                )}
              </span>
              {r.notes && <span className="history-notes">{r.notes}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GoalHistory;
